import { Alert, FormControl, FormGroup, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormikCheckbox, HelpTooltip } from 'components';
import { SHOP_WEBHOOK_BASE_FIELDS } from 'constants/shops.constants';
import { OrderType, ShopIntegrationType, ShopWebhookField } from 'enums';
import { TranslationNamespace } from 'i18n';
import type { Shop } from 'types/shop.type';
import { shopUtils } from 'utils';

type Props = { orderType: OrderType } & Pick<Shop, 'integrationConfig'>;
type Values = Partial<
  Record<'payinWebhookFields' | 'payoutWebhookFields', ShopWebhookField[]>
>;

export const ShopWebhookFields: React.FC<Props> = ({
  orderType,
  integrationConfig,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage.fields',
  });
  const { values, initialValues } = useFormikContext<Values>();
  const options = useMemo(
    () =>
      shopUtils.getAvailableWebhookFields(orderType).map((field) => ({
        value: field,
        label: `${t(`webhook_field_options.${field}`)} (${field})`,
      })),
    [t, orderType],
  );
  const name = useMemo(
    () => shopUtils.getWebhookFieldsName(orderType),
    [orderType],
  );
  const hasChangedFields = useMemo(() => {
    const selectedFields = new Set(values[name] ?? []);
    const savedFields = new Set(initialValues[name] ?? []);
    return !isEqual(
      Array.from(selectedFields).sort(),
      Array.from(savedFields).sort(),
    );
  }, [values, initialValues, name]);
  const usesSpecialWebhook = useMemo(
    () =>
      integrationConfig?.enabled &&
      [
        ShopIntegrationType.RosaOne,
        ShopIntegrationType.SQ,
        ShopIntegrationType.BF,
      ].includes(integrationConfig.type),
    [integrationConfig],
  );
  const baseFields = useMemo(() => SHOP_WEBHOOK_BASE_FIELDS.join(', '), []);

  if (usesSpecialWebhook) {
    return null;
  }

  return (
    <FormControl>
      <FormControl sx={{ flexDirection: 'row', alignItems: 'center' }}>
        <Typography variant="subtitle1">
          {t(`${orderType}_webhook_fields`)}
        </Typography>
        <HelpTooltip
          title={
            <div>
              <Typography variant="inherit" paragraph>
                <strong>{t('webhook_base_fields')}</strong> {`${baseFields}.`}
              </Typography>
              <Typography variant="inherit" component="p">
                {t('webhook_extra_fields')}
              </Typography>
            </div>
          }
        />
      </FormControl>
      <FormGroup sx={{ mt: '16px' }}>
        {options.map((option) => (
          <FormikCheckbox
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
          />
        ))}
      </FormGroup>
      {hasChangedFields && (
        <Alert severity="warning">
          {t('webhook_fields_signature_warning')}
        </Alert>
      )}
    </FormControl>
  );
};
